<template>
  <b-modal
    v-model="visible"
    centered
    hide-header
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
    body-class="p-4"
    @hidden="onHidden"
  >
    <button type="button" class="close-btn" aria-label="Close" @click="close">
      &times;
    </button>

    <div class="text-center px-2">
      <b-icon-shield-lock class="otp-icon"/>

      <h4 class="font-weight-bold mt-4 mb-3">ระบบยืนยันตัวตน 2 ชั้น</h4>

      <p class="text-secondary mb-4">
        ระบบได้ส่งรหัส OTP {{ otpLength }} หลักไปยังอีเมล<br />
        <span class="font-weight-bold text-dark">{{ email }}</span
        ><br />
        กรุณากรอกรหัสเพื่อเปิดเข้าสู่บัญชีของคุณ
      </p>

      <div class="otp-inputs" @paste="onPaste">
        <input
          v-for="(digit, index) in digits"
          :key="index"
          :ref="'otp-' + index"
          v-model="digits[index]"
          type="text"
          inputmode="numeric"
          maxlength="1"
          class="otp-box"
          :class="{ 'is-invalid': hasError }"
          :disabled="isVerifying"
          @input="onInput(index, $event)"
          @keydown.delete="onBackspace(index, $event)"
          @focus="$event.target.select()"
        />
      </div>

      <p v-if="hasError" class="text-danger small mt-3 mb-0">
        {{ errorMessage }}
      </p>

      <p v-if="isExpired" class="text-danger mt-3 mb-0">
        รหัส OTP หมดอายุแล้ว กรุณาขอรหัสใหม่อีกครั้ง
      </p>
      <p v-else class="text-secondary mt-3 mb-0">
        รหัสจะหมดอายุภายใน {{ formattedCountdown }} นาที
      </p>

      <b-button
        variant="primary"
        block
        class="mt-3 confirm-btn"
        :disabled="!isComplete || isVerifying || isExpired"
        @click="verify"
      >
        <span
          v-if="isVerifying"
          class="spinner-border spinner-border-sm mr-1"
          role="status"
          aria-hidden="true"
        ></span>
        ยืนยันรหัส
      </b-button>

      <p class="text-secondary mt-3 mb-0">
        ยังไม่ได้รับรหัส?
        <a
          href="#"
          class="resend-link"
          :class="{ disabled: isRequesting }"
          @click.prevent="resend"
          >ส่งอีกครั้ง</a
        >
      </p>
    </div>
  </b-modal>
</template>

<script>
import otpService from "@/services/otp";

export default {
  name: "OtpVerificationModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    comId: {
      type: [Number, String],
      required: true
    },
    mId: {
      type: [Number, String],
      required: true
    },
    email: {
      type: String,
      default: ""
    },
    otpLength: {
      type: Number,
      default: 6
    },
    ttlMinutes: {
      type: Number,
      default: 5
    }
  },
  data() {
    return {
      visible: this.show,
      digits: Array(this.otpLength).fill(""),
      remainingSeconds: this.ttlMinutes * 60,
      timer: null,
      referenceCode: "",
      isRequesting: false,
      isVerifying: false,
      hasError: false,
      errorMessage: ""
    };
  },
  computed: {
    code() {
      return this.digits.join("");
    },
    isComplete() {
      return this.code.length === this.otpLength;
    },
    isExpired() {
      return this.remainingSeconds <= 0;
    },
    formattedCountdown() {
      const minutes = Math.floor(this.remainingSeconds / 60);
      const seconds = this.remainingSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }
  },
  watch: {
    show(value) {
      this.visible = value;
      if (value) {
        this.requestOtp();
      }
    }
  },
  beforeDestroy() {
    this.stopCountdown();
  },
  methods: {
    async requestOtp() {
      this.reset();
      this.isRequesting = true;
      try {
        const { data } = await otpService.requestOtp({ comId: this.comId, mId: this.mId });
        this.referenceCode = data.referenceCode;
        this.startCountdown();
        this.$nextTick(() => this.focusInput(0));
      } catch (error) {
        this.showError("ไม่สามารถส่งรหัส OTP ได้ กรุณาลองใหม่อีกครั้ง");
      } finally {
        this.isRequesting = false;
      }
    },
    async verify() {
      if (!this.isComplete || this.isExpired) return;
      this.isVerifying = true;
      this.hasError = false;
      try {
        await otpService.verifyOtp({
          comId: this.comId,
          mId: this.mId,
          otpCode: this.code,
          referenceCode: this.referenceCode
        });
        this.stopCountdown();
        this.$emit("verified");
      } catch (error) {
        const status = error.response && error.response.status;
        if (status === 404) {
          this.showError("รหัส OTP ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง");
        } else {
          this.showError("เกิดข้อผิดพลาดในการยืนยันรหัส กรุณาลองใหม่อีกครั้ง");
        }
        this.clearDigits();
        this.focusInput(0);
      } finally {
        this.isVerifying = false;
      }
    },
    resend() {
      if (this.isRequesting) return;
      this.requestOtp();
    },
    onInput(index, event) {
      this.hasError = false;
      // keep digits numeric only
      const value = event.target.value.replace(/\D/g, "");
      this.$set(this.digits, index, value.slice(-1));
      if (this.digits[index] && index < this.otpLength - 1) {
        this.focusInput(index + 1);
      }
    },
    onBackspace(index, event) {
      if (!this.digits[index] && index > 0) {
        event.preventDefault();
        this.$set(this.digits, index - 1, "");
        this.focusInput(index - 1);
      }
    },
    onPaste(event) {
      event.preventDefault();
      const pasted = (event.clipboardData || window.clipboardData)
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, this.otpLength);
      if (!pasted) return;
      this.hasError = false;
      for (let i = 0; i < this.otpLength; i++) {
        this.$set(this.digits, i, pasted[i] || "");
      }
      const next = Math.min(pasted.length, this.otpLength - 1);
      this.focusInput(next);
    },
    focusInput(index) {
      const input = this.$refs["otp-" + index];
      const el = Array.isArray(input) ? input[0] : input;
      if (el) el.focus();
    },
    clearDigits() {
      this.digits = Array(this.otpLength).fill("");
    },
    showError(message) {
      this.hasError = true;
      this.errorMessage = message;
    },
    startCountdown() {
      this.stopCountdown();
      this._deadline = Date.now() + this.ttlMinutes * 60 * 1000;
      this._tickCountdown();
      this.timer = setInterval(() => this._tickCountdown(), 1000);
    },
    _tickCountdown() {
      const remaining = Math.round((this._deadline - Date.now()) / 1000);
      this.remainingSeconds = remaining > 0 ? remaining : 0;
      if (this.remainingSeconds <= 0) {
        this.stopCountdown();
      }
    },
    stopCountdown() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    reset() {
      this.clearDigits();
      this.hasError = false;
      this.errorMessage = "";
      this.stopCountdown();
      this.remainingSeconds = this.ttlMinutes * 60;
    },
    close() {
      this.visible = false;
    },
    onHidden() {
      this.reset();
      this.$emit("close");
      this.$emit("update:show", false);
    }
  }
};
</script>

<style scoped>
.otp-icon {
  width: 56px;
  height: 56px;
  color: #df812f;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1.25rem;
  border: none;
  background: transparent;
  font-size: 1.5rem;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
}

.otp-inputs {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.otp-box {
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  outline: none;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.otp-box:focus {
  border-color: #df812f;
  box-shadow: 0 0 0 0.2rem rgba(223, 129, 47, 0.25);
}

.otp-box.is-invalid {
  border-color: #dc3545;
}

.confirm-btn {
  border-radius: 0.5rem;
}

.resend-link {
  color: #007bff;
  font-weight: 600;
  text-decoration: underline;
}

.resend-link.disabled {
  pointer-events: none;
  opacity: 0.5;
}
</style>
